import type { MountableApp } from '@opengov/psp-contract';
import type { Logger, RemoteModuleConfig } from '../types';

/**
 * Default time between successive `healthCheck()` invocations. The shell
 * polls every 30 seconds after a successful mount; the architectural-review
 * write-up at https://opengovinc.atlassian.net/wiki/spaces/PSP/pages/5972787291
 * (§P2-18) selected this cadence as the cheap-but-useful sampling rate for
 * runtime health signals.
 */
export const DEFAULT_MODULE_HEALTH_CHECK_INTERVAL_MS = 30_000;

export interface StartModuleHealthPollOptions {
  /** The mounted module whose `healthCheck()` is polled. */
  module: MountableApp;
  /** App registry identity included in every structured log payload. */
  appConfig: Pick<RemoteModuleConfig, 'id' | 'basePath'>;
  /**
   * Logger used to emit structured Faro telemetry. The helper only calls
   * `info` and `warn`. Calls are wrapped so a throwing logger cannot escape
   * the interval callback or affect the mounted module.
   */
  logger: Pick<Logger, 'info' | 'warn'>;
  /**
   * Test seam — production callers never pass this. Vitest specs use
   * `intervalMs: 50` together with `vi.useFakeTimers()` for fast assertions
   * without slowing the suite.
   */
  intervalMs?: number;
}

/**
 * Start a passive health-check poll against a mounted remote module.
 *
 * The helper is intentionally NOT a React hook. The caller
 * (`useShellBridgeMount`) decides exactly when to start it (only after
 * `module.mount()` returns successfully) so that a sibling `useEffect`
 * cannot accidentally run a poll against a module whose mount threw.
 *
 * Behavior:
 *   - Returns immediately with a noop dispose when `module.healthCheck` is
 *     not a function (modules opt in by exporting the method; absence is
 *     not an error).
 *   - Otherwise: runs an immediate first check, then a `setInterval` every
 *     `intervalMs` (default 30s).
 *   - Logging is transition-based, not per-cycle, to keep Faro cardinality
 *     proportional to state changes rather than `O(time × modules)`:
 *       • first healthy result → `logger.info('Module health check first result')`
 *       • first unhealthy result OR healthy → unhealthy → `logger.warn('Module health check failed')`
 *       • unhealthy → healthy → `logger.info('Module health recovered')`
 *       • same-state transitions are silent.
 *   - A throw inside `healthCheck()` is caught and treated as `false`. The
 *     `errorName`/`error`/`stack` fields are folded into the same
 *     first/transition warning payload — there is no per-tick `Module
 *     health check threw` event.
 *   - Logger calls are wrapped (`safeInfo` / `safeWarn`); a failing logger
 *     never throws out of `runCheck` and never affects the mounted module.
 *   - The returned dispose function clears the interval and uses a
 *     `disposed` flag so any in-flight callback bails before mutating
 *     state.
 *
 * See `~/.cursor/plans/pss-2537-implementation_0ce2ab40.plan.md` §7.3 for
 * the full rationale and trade-off catalogue (notably: a throw that occurs
 * after the module is already in the unhealthy bucket does NOT log
 * `errorName`/`stack` because no transition fires — intentional per §10
 * decision 5).
 */
export function startModuleHealthPoll({
  module,
  appConfig,
  logger,
  intervalMs = DEFAULT_MODULE_HEALTH_CHECK_INTERVAL_MS,
}: StartModuleHealthPollOptions): () => void {
  if (typeof module.healthCheck !== 'function') {
    return () => {};
  }

  let previousResult: boolean | null = null;
  let disposed = false;

  const safeInfo: Logger['info'] = (...args) => {
    try {
      logger.info(...args);
    } catch {
      // Health telemetry is best-effort; never let logger failures affect the mounted module.
    }
  };

  const safeWarn: Logger['warn'] = (...args) => {
    try {
      logger.warn(...args);
    } catch {
      // Health telemetry is best-effort; never let logger failures affect the mounted module.
    }
  };

  const runCheck = () => {
    if (disposed) return;

    let healthy = false;
    let thrownError: Error | null = null;
    try {
      // Strict `=== true` check: forces module authors to honor the typed
      // `boolean` contract rather than relying on truthy coercion.
      healthy = module.healthCheck!() === true;
    } catch (err) {
      thrownError = err instanceof Error ? err : new Error(String(err));
      healthy = false;
    }

    const previous = previousResult;
    const isFirst = previous === null;
    const wentUnhealthy = healthy === false && (isFirst || previous === true);
    const recovered = healthy === true && previous === false;

    if (isFirst && healthy) {
      safeInfo('Module health check first result', {
        appId: appConfig.id,
        basePath: appConfig.basePath,
        healthy: true,
      });
    } else if (wentUnhealthy) {
      safeWarn('Module health check failed', {
        appId: appConfig.id,
        basePath: appConfig.basePath,
        healthy: false,
        transition: isFirst ? 'first' : 'healthy_to_unhealthy',
        ...(thrownError && {
          errorName: thrownError.name,
          error: thrownError.message,
          stack: thrownError.stack,
        }),
      });
    } else if (recovered) {
      safeInfo('Module health recovered', {
        appId: appConfig.id,
        basePath: appConfig.basePath,
        healthy: true,
        transition: 'unhealthy_to_healthy',
      });
    }

    previousResult = healthy;
  };

  runCheck();
  const timer = setInterval(runCheck, intervalMs);

  return () => {
    disposed = true;
    clearInterval(timer);
  };
}
